import { useCallback, useState } from 'react';
import { useLocationParams } from 'shared/routing/LocationParams';
import { useTrackChangeBankAccountEvent } from './ChangeBankAccountAnalytics';
import {
  BankAccountVerificationResult,
  ChangeBankAccountStep,
  ChangeBankAccountStepState,
  SubmittedBankAccount,
} from './ChangeBankAccountTypes';

const INITIAL_CHANGE_BANK_ACCOUNT_STEP_STATE: ChangeBankAccountStepState = {
  isSubmitting: false,
  hasUnsavedChanges: false,
};

interface UseChangeBankAccountFlowProps {
  onComplete: () => void;
}

export function useChangeBankAccountFlow({
  onComplete,
}: UseChangeBankAccountFlowProps) {
  const trackChangeBankAccountEvent = useTrackChangeBankAccountEvent();
  const [params, updateParams] = useLocationParams<{
    step?: ChangeBankAccountStep | null;
  }>();
  const [submittedBankAccount, setSubmittedBankAccount] =
    useState<SubmittedBankAccount>();
  const [stepState, setStepState] = useState<ChangeBankAccountStepState>(
    INITIAL_CHANGE_BANK_ACCOUNT_STEP_STATE,
  );

  const step: ChangeBankAccountStep =
    params.step === 'verify' ||
    (params.step === 'confirm' && submittedBankAccount)
      ? params.step
      : 'bank-info';

  const reset = useCallback(() => {
    setSubmittedBankAccount(undefined);
    setStepState(INITIAL_CHANGE_BANK_ACCOUNT_STEP_STATE);
    if (params.step) {
      updateParams({ step: null }, { navigation: 'replace' });
    }
  }, [params.step, updateParams]);

  const handleContinue = useCallback(
    (account: SubmittedBankAccount) => {
      setSubmittedBankAccount(account);
      updateParams({ step: 'confirm' }, { navigation: 'replace' });
      setStepState({ isSubmitting: false, hasUnsavedChanges: true });
    },
    [updateParams],
  );

  const handleConfirmed = useCallback(
    ({ status, verificationMethod }: BankAccountVerificationResult) => {
      if (status === 'verified' || verificationMethod === 'ach') {
        onComplete();
        return;
      }
      trackChangeBankAccountEvent(
        'Carrier Submitted Change Bank Account Verification Code',
      );
      updateParams({ step: 'verify' }, { navigation: 'replace' });
      setStepState({ isSubmitting: false, hasUnsavedChanges: false });
    },
    [onComplete, trackChangeBankAccountEvent, updateParams],
  );

  return {
    step,
    submittedBankAccount,
    stepState,
    setStepState,
    handleContinue,
    handleConfirmed,
    reset,
  };
}
